import {
  InputControl,
  InputPasswordControl,
  PasswordList,
  useAuthenticationPolicy,
  usePasswordPolicy,
  usePlatformConfig,
} from "@aurem/components";
import { Button, Divider, Text } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContainer } from "components/AuthContainer";
import { ContactSupportLink } from "components/ContactSupportLink";
import ROUTES from "const/routes";
import { useAcceptInvitation } from "hooks/useAcceptInvitation";
import useAnalytics, { AnalyticsEvent } from "hooks/useAnalytics";
import { useNavigateAfterInvitation } from "hooks/useNavigateAfterInvitation";
import { AuthStatus, useAuth } from "providers/AuthProvider";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";

import { FormProvider, useForm } from "react-hook-form";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { buildGoogleOAuth2URL, useSignOut } from "utils/auth";
import * as z from "zod";
import GoogleIcon from "../../../components/GoogleIcon";
import { useSignUp } from "../hooks/sign-up.hooks";

const SignUp: FC = () => {
  const { t, tRich } = useI18n();
  const { language } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthState } = useAuth();
  const platformConfig = usePlatformConfig();
  const signOut = useSignOut();
  const { track } = useAnalytics();
  const { signUp, loading } = useSignUp();
  const { validatePassword, policy } = usePasswordPolicy();
  const { authenticationPolicy } = useAuthenticationPolicy();
  const { acceptInvitation } = useAcceptInvitation();
  const getDestination = useNavigateAfterInvitation();

  const invitationId = searchParams.get("invitationId") || "";
  const invitationType = searchParams.get("type") || "admin";
  const isEmployee = invitationType === "employee";

  const formSchema = z.object({
    email: z
      .string()
      .nonempty(t("apps.employee.validation.email.required"))
      .email({ message: t("apps.employee.validation.email.format") }),
    password: z
      .string()
      .nonempty(t("apps.employee.validation.password.required")),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit({ email, password }: z.infer<typeof formSchema>) {
    try {
      validatePassword(password);
    } catch (err) {
      if (err instanceof Error) {
        form.setError("password", {
          type: "manual",
          message: err.message,
        });
        return;
      }
    }

    await signOut();
    let data;
    try {
      ({ data } = await signUp({
        variables: {
          email,
          password,
          isEmployee,
        },
      }));
    } catch (err) {
      form.setError("password", {
        type: "manual",
        message: err instanceof Error ? err.message : String(err),
      });
      return;
    }
    const tokens = data?.registerUser;
    if (tokens?.accessToken && tokens?.refreshToken) {
      setAuthState({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        status: AuthStatus.Authenticated,
      });
      track(AnalyticsEvent.SignUpByEmail);
      const invitationData = await acceptInvitation();
      const from = invitationData
        ? getDestination({ invitationData })
        : undefined;

      void navigate(`/${language}/admin/${ROUTES.DISCLAIMERS}`, {
        state: { from },
      });
    }
  }

  function handleGoogleSignUp() {
    window.location.href = buildGoogleOAuth2URL({
      action: "signup",
      invitationId,
      invitationType,
      language,
    });
  }

  const platformNameKey =
    "apps.admin.pages.controls.branding.platform.signUpName" as const;
  const platformName = t(platformNameKey)
    ? tRich(platformNameKey)
    : language === "ar"
      ? platformConfig.nameAr
      : platformConfig.name;

  return (
    <FormProvider {...form}>
      <AuthContainer platform="admin">
        <div className="flex flex-col gap-2 text-2xl mt-1">
          <h2 className="font-medium">
            {t("apps.admin.pages.signUp.page.signUp.title")} {platformName}
          </h2>

          <div className="flex gap-1 mt-1">
            <span className="text-sm font-normal text-neutral-400">
              {t("apps.admin.pages.signUp.page.signUp.alreadyHaveAccount")}
            </span>
            <Button
              as={Link}
              to={`/${language}/${ROUTES.SIGN_IN}`}
              variant="text"
              color="neutral"
              className="underline"
            >
              {t("apps.admin.pages.signUp.page.signUp.signInLink")}
            </Button>
          </div>
        </div>
        <form
          className="flex flex-col gap-6"
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-5">
            <InputControl
              data-e2e-id="signUp__input__email"
              label={t("apps.admin.pages.signUp.page.signUp.email.label")}
              name="email"
              required
            />
            <InputPasswordControl
              data-e2e-id="signUp__input__password"
              id="password"
              name="password"
              required
              label={t("apps.admin.pages.signUp.page.signUp.password.label")}
            />
          </div>
          <PasswordList
            policy={policy}
            translations={{
              intro: t("apps.admin.validation.list.intro"),
              capitalLetter: t("apps.admin.validation.list.capitalLetter"),
              lowercaseLetter: t("apps.admin.validation.list.lowercaseLetter"),
              number: t("apps.admin.validation.list.number"),
              specialCharacter: t(
                "apps.admin.validation.list.specialCharacter",
              ),
              minimumLength: t(
                "apps.admin.validation.list.minimumLength",
                policy?.minLength,
              ),
            }}
          />
          <div className="flex flex-col gap-4">
            <Button
              data-e2e-id="signUp__button__submit"
              loading={loading}
              type="submit"
            >
              {t("apps.admin.pages.signUp.page.signUp.signUp")}
            </Button>

            {authenticationPolicy?.googleSignInEnabled && (
              <>
                <Divider text={t("apps.admin.common.or")} />
                <Button
                  type="button"
                  variant="outlined"
                  color="neutral"
                  data-e2e-id="signUp__button__google"
                  onClick={handleGoogleSignUp}
                  endIcon={<GoogleIcon />}
                >
                  {t("apps.admin.pages.signUp.page.signUp.signUpWithGoogle")}
                </Button>
              </>
            )}
            <Text size="xs" className="text-center">
              {t("apps.admin.pages.signUp.page.signUp.agreeTerms")}
            </Text>
          </div>
        </form>
        <ContactSupportLink />
      </AuthContainer>
    </FormProvider>
  );
};

export default SignUp;
